import React, { useState, useEffect } from 'react';
import { Calendar, Clock, Globe, Lock, ArrowLeft, Loader2 } from 'lucide-react';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Button from '../ui/Button';
import Select from '../ui/Select';
import { businessHoursScheduler } from '../../utils/BusinessHoursScheduler';
import { US_TIMEZONES, getBrowserTimezone, getTimezoneAbbr, getTimezoneLabel } from '../../utils/timezones';
import { TCPACompliance } from '../../utils/tcpaCompliance';
import { clientConfig } from '../../config/clientConfig';

type ScheduleCallFormProps = {
  firstName: string;
  phoneNumber: string;
  onSchedule: (selectedTime: Date) => void;
};

type Step = 'date' | 'time' | 'confirm';

/**
 * ScheduleCallForm component now uses the new BusinessHoursScheduler with real availability data.
 * 
 * Key improvements:
 * - Real availability data from leads service API
 * - Smart caching (1 hour TTL) prevents duplicate API calls
 * - Loading states for better UX
 * - Error handling with fallback options
 * - Maintains all existing functionality
 */
export default function ScheduleCallForm({ firstName, phoneNumber, onSchedule }: ScheduleCallFormProps) {
  const [step, setStep] = useState<Step>('date');
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  const [timezone, setTimezone] = useState(getBrowserTimezone());
  const [loading, setLoading] = useState(true);
  const [availableSlots, setAvailableSlots] = useState<Date[]>([]);
  const [allSlots, setAllSlots] = useState<Date[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  // Load available slots using the new BusinessHoursScheduler with real data and caching
  useEffect(() => {
    const loadAvailableSlots = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const { availableSlots, allSlots } = await businessHoursScheduler.getAvailableTimeSlots();
        setAvailableSlots(availableSlots);
        setAllSlots(allSlots);
      } catch (err) {
        console.error('Error loading available time slots:', err);
        setError('Failed to load available time slots. Please try again.');
        // Set empty arrays as fallback
        setAvailableSlots([]);
        setAllSlots([]);
      } finally {
        setLoading(false);
      }
    };

         loadAvailableSlots();
   }, [timezone]); // Reload when timezone changes (cached data prevents unnecessary API calls)

   // Debug function to refresh availability data (development only)
   const handleRefreshAvailability = async () => {
     businessHoursScheduler.clearCache();
     setLoading(true);
     setError(null);
     
     try {
       const { availableSlots, allSlots } = await businessHoursScheduler.getAvailableTimeSlots(false);
       setAvailableSlots(availableSlots);
       setAllSlots(allSlots);
     } catch (err) {
       console.error('Error refreshing availability:', err);
       setError('Failed to refresh availability. Please try again.');
     } finally {
       setLoading(false);
     }
   };

  // Group time slots by date
  const groupedAvailableSlots = availableSlots.reduce((acc: Record<string, Date[]>, slot: Date) => {
    const dateKey = format(slot, 'yyyy-MM-dd');
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(slot);
    return acc;
  }, {} as Record<string, Date[]>);

  const groupedAllSlots = allSlots.reduce((acc: Record<string, Date[]>, slot: Date) => {
    const dateKey = format(slot, 'yyyy-MM-dd');
    if (!acc[dateKey]) {
      acc[dateKey] = [];
    }
    acc[dateKey].push(slot);
    return acc;
  }, {} as Record<string, Date[]>);

  const handleDateSelect = (dateKey: string) => {
    setSelectedDate(dateKey);
    setStep('time');
  };

  const handleTimeSelect = (time: Date) => {
    setSelectedTime(time);
    setStep('confirm');
  };

  const handleBack = () => {
    if (step === 'time') {
      setStep('date');
      setSelectedTime(null);
    } else if (step === 'confirm') {
      setStep('time');
    }
  };

  const handleSubmit = () => {
    if (selectedTime) {
      onSchedule(selectedTime);
    }
  };

  const handleTimezoneChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setTimezone(e.target.value);
    // Clear selections when timezone changes
    setSelectedDate(null);
    setSelectedTime(null);
    setStep('date');
    // Note: useEffect will reload slots automatically due to timezone dependency
  };

  const formatTimeSlot = (date: Date): string => {
    const zonedDate = utcToZonedTime(date, timezone);
    return format(zonedDate, 'h:mm a');
  };

  const formatDate = (date: Date): string => {
    const zonedDate = utcToZonedTime(date, timezone);
    return format(zonedDate, 'EEEE, MMMM d');
  };

  const formatDateTime = (date: Date): string => {
    const zonedDate = utcToZonedTime(date, timezone);
    return format(zonedDate, "EEEE, MMMM d 'at' h:mm a");
  };

  const isSlotAvailable = (slot: Date): boolean => {
    return availableSlots.some((availableSlot: Date) => 
      availableSlot.getTime() === slot.getTime()
    );
  };

  return (
    <div id="schedule-call-form" className="space-y-6">
      <div className="text-center">
        <Title 
          id="schedule-call-title"
          name="schedule-call-title"
          as="h1" 
          className="mb-2"
        >
          Schedule a Call
        </Title>
        <Description
          id="schedule-call-description"
          name="schedule-call-description"
        >
          Hi {firstName}, our team is currently busy or your calling outside of business hours. Please select a convenient time for us to call you back at {phoneNumber}.
        </Description>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
        <div className="flex items-center gap-3">
          <Globe className="w-5 h-5 text-gray-600" />
          <Select
            id="schedule-call-timezone"
            name="schedule-call-timezone"
            options={US_TIMEZONES.map(tz => ({
              value: tz.value,
              label: `${tz.label} (${tz.abbr})`
            }))}
            value={timezone}
            onChange={handleTimezoneChange}
            className="!text-base"
          />
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 text-[#212d52] animate-spin" />
          <span className="ml-2 text-gray-600">Loading available time slots...</span>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center gap-2 text-red-800">
            <Clock className="w-5 h-5" />
            <span className="font-medium">Unable to load time slots</span>
          </div>
          <p className="text-red-700 mt-1">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-2 text-red-800 underline hover:no-underline"
          >
            Refresh page to try again
          </button>
        </div>
      )}

      {!loading && !error && step === 'date' && (
        <div className="space-y-4">
          {Object.keys(groupedAllSlots).length === 0 ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-yellow-800">
                <Calendar className="w-5 h-5" />
                <span className="font-medium">No available time slots</span>
              </div>
              <p className="text-yellow-700 mt-1">
                Currently no appointment slots are available. Please try again later or contact us directly.
              </p>
            </div>
                     ) : (
             Object.entries(groupedAllSlots).map(([dateKey, slots]: [string, Date[]]) => {
               const availableCount = groupedAvailableSlots[dateKey]?.length || 0;
               return (
                 <button
                   key={dateKey}
                   id={`schedule-call-date-${dateKey}`}
                   name={`schedule-call-date-${dateKey}`}
                   onClick={() => handleDateSelect(dateKey)}
                   className="w-full p-4 bg-white rounded-lg border border-gray-200 hover:border-[#212d52] transition-colors text-left"
                 >
                   <div className="flex items-center gap-3">
                     <Calendar className="w-5 h-5 text-[#212d52]" />
                     <div>
                       <div className="font-medium text-gray-900">{formatDate(slots[0])}</div>
                       <div className="text-sm text-gray-600">
                         {availableCount} of {slots.length} times available
                       </div>
                     </div>
                   </div>
                 </button>
               );
             })
          )}
        </div>
      )}

      {!loading && !error && step === 'time' && selectedDate && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <button
              id="schedule-call-back-to-dates"
              name="schedule-call-back-to-dates"
              onClick={handleBack}
              className="text-[#212d52] hover:text-[#1a2441] font-medium"
            >
              <span className="flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" />
                Back to dates
              </span>
            </button>
            <div className="font-medium text-gray-900">
              {formatDate(groupedAllSlots[selectedDate][0])}
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {groupedAllSlots[selectedDate]?.map((slot: Date) => {
              const isAvailable = isSlotAvailable(slot);
              const isSelected = selectedTime?.toISOString() === slot.toISOString();
              
              return (
                <button
                  key={slot.toISOString()}
                  id={`schedule-call-time-${format(slot, 'HHmm')}`}
                  name={`schedule-call-time-${format(slot, 'HHmm')}`}
                  onClick={() => isAvailable && handleTimeSelect(slot)}
                  disabled={!isAvailable}
                  className={`
                    flex items-center justify-center gap-2 p-3 rounded-lg border transition-colors
                    ${isSelected
                      ? 'bg-[#212d52] text-white border-[#212d52]'
                      : isAvailable
                        ? 'bg-white text-gray-700 border-gray-200 hover:border-[#212d52]'
                        : 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                    }
                  `}
                >
                  {isAvailable ? (
                    <Clock className="w-4 h-4" />
                  ) : (
                    <Lock className="w-4 h-4" />
                  )}
                  <span className="text-sm font-medium">
                    {formatTimeSlot(slot)}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {!loading && !error && step === 'confirm' && selectedTime && (
        <div className="space-y-6">
          <button
            id="schedule-call-back-to-times"
            name="schedule-call-back-to-times"
            onClick={handleBack}
            className="text-[#212d52] hover:text-[#1a2441] font-medium"
          >
            <span className="flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Change time
            </span>
          </button>

          <div className="bg-white p-6 rounded-lg border border-gray-200 space-y-4">
            <div className="flex items-center justify-center gap-2 text-[#b3905e]">
              <Calendar className="w-5 h-5" />
              <span className="font-medium">Appointment Details</span>
            </div>
            
            <div className="text-center space-y-2">
              <p className="text-gray-600">Your callback is scheduled for:</p>
              <p className="text-xl font-semibold text-gray-900">
                {formatDateTime(selectedTime)}
              </p>
              <p className="text-sm text-gray-500">
                {getTimezoneLabel(timezone)} ({getTimezoneAbbr(timezone)})
              </p>
            </div>

            <Button
              id="schedule-call-confirm"
              name="schedule-call-confirm"
              onClick={handleSubmit}
              fullWidth
              size="lg"
            >
              Schedule Call
            </Button>
          </div>
        </div>
      )}

      <Description 
        id="schedule-call-timezone-note"
        name="schedule-call-timezone-note"
        size="sm" 
        className="text-center"
      >
        All times shown in {getTimezoneLabel(timezone)} ({getTimezoneAbbr(timezone)})
      </Description>

      {/* Development-only debug controls */}
      {clientConfig.isDebugEnabled && (
        <div className="mt-4 p-3 bg-gray-100 rounded-lg border">
          <div className="text-xs text-gray-600 mb-2">
            🔧 Debug Controls (Development Only)
          </div>
          <button
            onClick={handleRefreshAvailability}
            disabled={loading}
            className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 disabled:opacity-50"
          >
            {loading ? 'Refreshing...' : 'Refresh Availability (Clear Cache)'}
          </button>
          <div className="text-xs text-gray-500 mt-1">
            Cache stats: {businessHoursScheduler.getCacheStats().apiCache} API entries, {businessHoursScheduler.getCacheStats().slotsCache} slots entries
          </div>
        </div>
      )}
    </div>
  );
}